import Rollbar from "rollbar";

type RollbarUser = {
  id?: string;
  email?: string;
  name?: string;
};

type RollbarPerson = {
  id: string | number | null;
  email?: string;
  username?: string;
};

let rollbarInstance: Rollbar | null = null;
let currentRollbarUser: RollbarUser | null = null;

const getRollbarAccessToken = () =>
  process.env.NEXT_PUBLIC_ROLLBAR_POST_CLIENT_ITEM_ACCESS_TOKEN ||
  process.env.ROLLBAR_POST_CLIENT_ITEM_ACCESS_TOKEN;

const getRollbarEnvironment = () =>
  process.env.NEXT_PUBLIC_ENV || process.env.NODE_ENV || "development";

const getPersonPayload = (): RollbarPerson | undefined => {
  if (!currentRollbarUser) {
    return undefined;
  }

  const person: RollbarPerson = {
    id:
      currentRollbarUser.id ??
      currentRollbarUser.email ??
      currentRollbarUser.name ??
      null,
  };

  if (currentRollbarUser.email) {
    person.email = currentRollbarUser.email;
  }
  if (currentRollbarUser.name) {
    person.username = currentRollbarUser.name;
  }

  if (person.id === null && !person.email && !person.username) {
    return undefined;
  }

  return person;
};

export const initializeErrorTracking = () => {
  const accessToken = getRollbarAccessToken();

  if (!rollbarInstance && accessToken) {
    rollbarInstance = new Rollbar({
      accessToken,
      environment: getRollbarEnvironment(),
      enabled: process.env.NODE_ENV !== "test",
      captureUncaught: true,
      captureUnhandledRejections: true,
      payload: {
        person: getPersonPayload(),
        client: {
          javascript: {
            code_version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || "local",
            source_map_enabled: true,
            guess_uncaught_frames: true,
          },
        },
      },
    });
  }
  return rollbarInstance;
};

export const setRollbarUser = (user: RollbarUser | null) => {
  currentRollbarUser = user;

  const rollbar = initializeErrorTracking();
  if (!rollbar) {
    return;
  }

  rollbar.configure({
    payload: {
      person: getPersonPayload() || { id: null },
    },
  });
};

const withCurrentUser = (extra?: Record<string, unknown>) => {
  const person = getPersonPayload();

  if (!person) {
    return extra;
  }

  return {
    ...extra,
    person,
  };
};

export const trackError = (error: unknown, extra?: Record<string, unknown>) => {
  const rollbar = initializeErrorTracking();
  if (rollbar) {
    rollbar.error(error as Error, withCurrentUser(extra));
  }
};

export const trackInfo = (message: string, extra?: Record<string, unknown>) => {
  const rollbar = initializeErrorTracking();
  if (rollbar) {
    rollbar.info(message, withCurrentUser(extra));
  }
};
